import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";

import { unsubscribeShipmentAsGuestUser } from "api";
import Button from "components/v2/Button/Button";
import Modal from "components/v2/Modal/Modal";
import errorToast from "helpers/errorToast";

interface UnsubscribeDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function UnsubscribeDialog({ isOpen, onClose }: UnsubscribeDialogProps) {
  const { id } = useParams();
  const intl = useIntl();
  const navigate = useNavigate();

  const { mutate, isLoading } = useMutation({
    mutationFn: () => unsubscribeShipmentAsGuestUser(id),
    onSuccess: () => {
      toast(
        intl.formatMessage({
          id: "components.molecules.navigation.unsubscribe.success",
          defaultMessage: "Successfully unsubscribed from shipment",
        }),
        { type: "success" },
      );
      onClose();
      navigate("/shipments");
    },
    onError: (err) => {
      errorToast(
        err ??
          intl.formatMessage({
            id: "components.molecules.navigation.unsubscribe.fail",
            defaultMessage: "Fail to subscribe from shipment",
          }),
      );
    },
  });

  const handleConfirm = () => {
    mutate();
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="md">
      <Modal.Header>
        <div className="mt-3xl text-tertiary text-sm">
          <FormattedMessage
            id="components.molecules.navigation.unsubscribe.confirmCancelText"
            defaultMessage="Are you sure you want to unsubscribe to this shipment?"
          />
        </div>
      </Modal.Header>
      <Modal.Footer>
        <div className="d-flex align-items-center justify-content-end">
          <div className="mr-lg">
            <Button
              label={intl.formatMessage({
                id: "general.cancel",
                defaultMessage: "Cancel",
              })}
              onClick={onClose}
              variant="secondary-gray"
            />
          </div>
          <Button
            label={intl.formatMessage({
              id: "general.confirm",
              defaultMessage: "Confirm",
            })}
            onClick={handleConfirm}
            variant="error"
            disabled={isLoading}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
}
